// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import ResponsiveButton, {
    ResponsiveButtonPresentationProps,
} from "@store/Library/Components/Toolkit/ResponsiveButton";
import getButtonVariantField from "@store/Framework/Common/Fields/Toolkit/buttonVariant";
import getButtonSizeVariantField from "@store/Framework/Common/Fields/Toolkit/buttonSizeVariant";
import { ButtonSizeVariants } from "@store/MobiusOverrides/Button/Button";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import PreTitle from "@store/Library/Components/PreTitle/PreTitle";
import getColor from "@insite/mobius/utilities/getColor";
import Zone from "@insite/client-framework/Components/Zone";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useMemo } from "react";

const enum fields {
    preTitle = "preTitle",
    title = "title",
    description = "description",
    ctaText = "ctaText",
    ctaLink = "ctaLink",
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    imageSide = "imageSide",
    customCSS = "customCSS",
    ctaFullWidth = "ctaFullWidth",
    ctaVariant = "ctaVariant",
    ctaSizeVariant = "ctaSizeVariant",
}

const ctaVariantField = getButtonVariantField(fields.ctaVariant);
const ctaSizeVariantField = getButtonSizeVariantField(fields.ctaSizeVariant);

interface OwnProps extends WidgetProps {
    fields: {
        [fields.preTitle]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.ctaText]: string;
        [fields.ctaLink]: LinkFieldValue;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.imageSide]: "left" | "right";
        [fields.customCSS]: string;
        [fields.ctaFullWidth]: boolean;
        [fields.ctaVariant]: "primary" | "secondary" | "tertiary";
        [fields.ctaSizeVariant]: ButtonSizeVariants;
    };
}

export interface ContentCTAStyles {
    wrapper?: InjectableCss;
    layout?: InjectableCss;
    imageColumn?: InjectableCss;
    contentColumn?: InjectableCss;
    headingBlock?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    ctaButton?: ResponsiveButtonPresentationProps;
}

export const contentCTAStyles: ContentCTAStyles = {
    wrapper: {
        css: spacingStyles(),
    },
    layout: {
        css: css`
            display: grid;
            grid-template-columns: 1fr;
            gap: ${pxToRem(24)};

            ${breakpoint("md")} {
                grid-template-columns: 1fr 1fr;
                gap: ${pxToRem(80)};
            }
        `,
    },
    imageColumn: {
        css: css`
            width: 100%;
            align-self: start;
        `,
    },
    contentColumn: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(16)};

            ${breakpoint("md")} {
                height: 100%;
                justify-content: center;
            }
        `,
    },
    headingBlock: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(10)};
            min-width: 0;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin: 0;
        `,
    },
    description: {
        variant: "p",
        size: "large",
        css: css`
            margin: 0;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            aspect-ratio: 3 / 2;
            border-radius: ${pxToRem(8)};
            overflow: hidden;
            position: relative;
            background: ${getColor("common.background")};

            > * {
                display: block;
                width: 100%;
                height: 100%;
            }

            * {
                width: 100%;
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
            background-color: transparent;
        `,
    },
    ctaButton: {
        css: css`
            width: auto;
            align-self: flex-start;
            display: inline-flex;
        `,
        mergeCss: true,
    },
};

const defaultCustomCss = `.layout {
}

.imageColumn {
}

.contentColumn {
}

.headingBlock {
}

imgWrapper {
}

imgLink {
}

.img {
}

.ctaButton {
}
`;

export const ContentCTAWidget: FC<OwnProps> = ({ fields, id }) => {
    const imageLink = useGetLink(fields.imageLink);
    const ctaLink = useGetLink(fields.ctaLink);

    const styles = useMergeStyles("image", contentCTAStyles);

    const isImageLeft = (fields.imageSide || "left") === "left";

    const imageColumnStyles = useMemo(
        () =>
            mergeProps<InjectableCss>(styles.imageColumn, {
                css: css`
                    ${styles.imageColumn?.css}
                    order: 0;

                    ${breakpoint("md")} {
                        order: ${isImageLeft ? 0 : 2};
                    }
                `,
            }),
        [isImageLeft, styles.imageColumn],
    );

    const contentColumnStyles = useMemo(
        () =>
            mergeProps<InjectableCss>(styles.contentColumn, {
                css: css`
                    ${styles.contentColumn?.css}
                    order: 1;

                    ${breakpoint("md")} {
                        order: 1;
                    }
                `,
            }),
        [styles.contentColumn],
    );

    const image = <Img className="img" src={fields.imageUrl} altText={fields.altText} loading="lazy" {...styles.img} />;

    const imageBlock = (
        <StyledWrapper className="imageColumn" {...imageColumnStyles}>
            <StyledWrapper className="imgWrapper" {...styles.imageWrapper}>
                {imageLink?.url ? (
                    <Link className="imgLink" href={imageLink.url} {...styles.imageLink}>
                        {image}
                    </Link>
                ) : (
                    image
                )}
            </StyledWrapper>
        </StyledWrapper>
    );

    const ctaButtonStyles: ResponsiveButtonPresentationProps = useMemo(() => {
        return {
            ...styles.ctaButton,
            variant: ctaVariantField.parse(fields.ctaVariant),
            sizeVariant: ctaSizeVariantField.parse(fields.ctaSizeVariant),
            css: css`
                width: ${fields.ctaFullWidth ? "100%" : "auto"};
                align-self: flex-start;
                display: inline-flex;
            `,
            mergeCss: true,
        };
    }, [fields.ctaFullWidth, fields.ctaVariant, fields.ctaSizeVariant, styles.ctaButton]);

    const contentBlock = (
        <StyledWrapper className="contentColumn" {...contentColumnStyles}>
            <Zone contentId={id} zoneName="Top" />
            {(!!fields.preTitle || !!fields.title) && (
                <StyledWrapper className="headingBlock" {...styles.headingBlock}>
                    {!!fields.preTitle && <PreTitle text={fields.preTitle} />}
                    {!!fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
                </StyledWrapper>
            )}
            {!!fields.description && <Typography {...styles.description}>{fields.description}</Typography>}
            {!!fields.ctaText && !!ctaLink?.url && (
                <ResponsiveButton {...ctaButtonStyles} className="ctaButton" href={ctaLink.url} target="_self">
                    {fields.ctaText}
                </ResponsiveButton>
            )}
            <Zone contentId={id} zoneName="Bottom" />
        </StyledWrapper>
    );

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css}
            ${fields.customCSS}

            .layout {
                ${styles.layout?.css}
            }
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="layout">
                {imageBlock}
                {contentBlock}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = { displayName: "Basic", sortOrder: 0 };
const advancedTab = { displayName: "Advanced", sortOrder: 1 };

const widgetModule: WidgetModule = {
    component: ContentCTAWidget,
    definition: {
        displayName: "ContentCTA",
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.preTitle,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.description,
                editorTemplate: "MultilineTextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.ctaText,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.ctaLink,
                editorTemplate: "LinkField",
                fieldType: "Contextual",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                ...ctaVariantField.definition,
                fieldType: "General",
                tab: basicTab,
            },
            {
                ...ctaSizeVariantField.definition,
                tab: basicTab,
            },
            {
                name: fields.ctaFullWidth,
                displayName: "CTA Full Width",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.imageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.imageSide,
                displayName: "Image Side",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
